// "This looks like the same deal" (module 10 U260; DEAL-WORK-BRIEF-2026-10-04.md sections 3 and 4; REQ-2026-10-05-02,
// the Deal work lane's W3). Mike chose "Cobrain matches, you confirm": the line sits above "Latest" on the card and at
// the top of the history page, names what would move and from when, and offers "Bring its history here" and "Not this
// one". Nothing moves without a press; the join says what it brought in, with an Undo (D62). The server offers a
// candidate only to Commercial and admins, so anyone else sees nothing here.
//
// W5 (REQ-2026-10-05-12): where no matter is offered, the candidate may be a thread filed under no matter that names the
// company, and the line says so; and where the company holds no mail domain, the domain on its mail is put to the reader
// to confirm, with an Undo, so its mail reaches its record.
import { ActionButton } from '../parts/index.js';
import { useEffect, useState, type ReactNode } from 'react';
import { dayMonth } from '../day.js';
import { dealWorkApi, DealWorkError, type DomainAdded, type DomainToConfirm, type HistoryOf, type Joined, type SameDeal as Same } from './api.js';
import './dealwork.css';

const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

/** "9 messages, 1 meeting, 10 files and 11 commitments": what would move, by kind, the empty kinds left out. */
export function whatMoves(s: Pick<Same, 'messages' | 'meetings' | 'documents' | 'commitments'>): string {
  const parts = [
    s.messages ? n(s.messages, 'message', 'messages') : null,
    s.meetings ? n(s.meetings, 'meeting', 'meetings') : null,
    s.documents ? n(s.documents, 'file', 'files') : null,
    s.commitments ? n(s.commitments, 'commitment', 'commitments') : null,
  ].filter((x): x is string => x !== null);
  return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

/** "10 items, 11 commitments and 11 tasks": what a join moved. A filing is a message or a meeting, so it is an item. */
export function brought(m: Joined['moved']): string {
  const parts = [
    m.filings ? n(m.filings, 'item', 'items') : null,
    m.commitments ? n(m.commitments, 'commitment', 'commitments') : null,
    m.tasks ? n(m.tasks, 'task', 'tasks') : null,
  ].filter((x): x is string => x !== null);
  if (parts.length === 0) return 'nothing more';
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

export function SameDeal({ store, dealId, onChanged }: { store: string; dealId: string; onChanged?: () => void }) {
  const [same, setSame] = useState<Same | null>(null);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState<(Joined & { thread: boolean }) | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const [domain, setDomain] = useState<DomainToConfirm | null>(null);
  const [added, setAdded] = useState<DomainAdded | null>(null);
  const [domainSaid, setDomainSaid] = useState<string | null>(null);
  const [domainWrong, setDomainWrong] = useState<string | null>(null);

  const read = (withDomain: boolean) => dealWorkApi.sameDeal(store, dealId).then((r) => { setSame(r.sameDeal); if (withDomain) setDomain(r.domain ?? null); });
  useEffect(() => {
    let live = true;
    dealWorkApi.sameDeal(store, dealId).then((r) => { if (live) { setSame(r.sameDeal); setDomain(r.domain ?? null); } }).catch(() => undefined);
    return () => { live = false; };
  }, [store, dealId]);

  const of = (s: Same): HistoryOf => (s.thread !== null ? { thread: s.thread } : { matterId: s.matterId! });
  const fail = (e: unknown) => { setBusy(false); setWrong(e instanceof DealWorkError ? e.message : 'Nothing was changed. Try again.'); };
  const failDomain = (e: unknown) => { setBusy(false); setDomainWrong(e instanceof DealWorkError ? e.message : 'Nothing was changed. Try again.'); };
  const join = () => {
    if (!same) return;
    setBusy(true); setWrong(null);
    dealWorkApi.join(store, dealId, of(same)).then((j) => { setBusy(false); setJoined({ ...j, thread: same.thread !== null }); setSame(null); onChanged?.(); }).catch(fail);
  };
  const refuse = () => {
    if (!same) return;
    setBusy(true); setWrong(null);
    dealWorkApi.notSame(store, dealId, of(same)).then(() => { setBusy(false); setSaid(`Not this one: ${same.title} will not be offered here again.`); setSame(null); }).catch(fail);
  };
  const undo = () => {
    if (!joined) return;
    setBusy(true); setWrong(null);
    dealWorkApi.undoJoin(store, dealId, joined.actionId).then(() => {
      setBusy(false);
      setSaid(joined.thread ? `Put back: ${joined.title} is filed under nothing again.` : `Put back: ${joined.title} has its history again.`);
      setJoined(null);
      read(false).catch(() => undefined);
      onChanged?.();
    }).catch(fail);
  };
  const addDomain = () => {
    if (!domain) return;
    setBusy(true); setDomainWrong(null); setDomainSaid(null);
    dealWorkApi.addDomain(store, dealId, domain.domain).then((a) => { setBusy(false); setAdded(a); setDomain(null); onChanged?.(); }).catch(failDomain);
  };
  const notDomain = () => {
    if (!domain) return;
    setBusy(true); setDomainWrong(null);
    dealWorkApi.notDomain(store, dealId, domain.domain).then(() => {
      setBusy(false); setDomainSaid(`Not theirs: ${domain.domain} will not be put to you for ${domain.company} again.`); setDomain(null);
    }).catch(failDomain);
  };
  const undoDomain = () => {
    if (!added) return;
    setBusy(true); setDomainWrong(null);
    dealWorkApi.undoDomain(store, dealId, added.actionId).then(() => {
      setBusy(false); setDomainSaid(`Taken back: ${added.company} has no mail domain again.`); setAdded(null);
      read(true).catch(() => undefined);
      onChanged?.();
    }).catch(failDomain);
  };

  let history: ReactNode = null;
  if (joined) {
    history = (
      <div className="dw-same" role="status">
        <p>Brought in from {joined.title}: {brought(joined.moved)}.</p>
        <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo</ActionButton>
        {wrong ? <p className="dl-err" role="alert">{wrong}</p> : null}
      </div>
    );
  } else if (!same) {
    history = said || wrong ? <div className="dw-same">{said ? <p role="status">{said}</p> : null}{wrong ? <p className="dl-err" role="alert">{wrong}</p> : null}</div> : null;
  } else {
    const thread = same.thread !== null;
    history = (
      <div className="dw-same" role="region" aria-label="This looks like the same deal">
        {said ? <p role="status">{said}</p> : null}
        <p>This looks like the same deal: <b>{same.title}</b>, {n(same.items, 'item', 'items')}{same.since ? ` since ${dayMonth(new Date(same.since))}` : ''}{same.hubspotDealId ? ', put aside from HubSpot' : ''}{thread ? ', filed under nothing' : ''}.</p>
        <p className="dw-same-what">{whatMoves(same)} would {thread ? 'be filed' : 'move'} here.</p>
        <div className="dw-acts">
          <ActionButton type="button" className="dl-opt" disabled={busy} onClick={join}>Bring its history here</ActionButton>
          <ActionButton type="button" className="hx-link" disabled={busy} onClick={refuse}>Not this one</ActionButton>
        </div>
        {wrong ? <p className="dl-err" role="alert">{wrong}</p> : null}
      </div>
    );
  }

  let mail: ReactNode = null;
  if (added) {
    mail = (
      <div className="dw-same" role="status">
        <p>{added.domain} is {added.company}&apos;s now: its mail reaches the company.</p>
        <ActionButton type="button" className="hx-link" disabled={busy} onClick={undoDomain}>Undo</ActionButton>
        {domainWrong ? <p className="dl-err" role="alert">{domainWrong}</p> : null}
      </div>
    );
  } else if (domain) {
    mail = (
      <div className="dw-same" role="region" aria-label="A mail domain to confirm">
        {domainSaid ? <p role="status">{domainSaid}</p> : null}
        <p><b>{domain.company}</b> has no mail domain, so its mail does not reach its record. {domain.domain} is on {domain.items} of its messages, both ways.</p>
        <div className="dw-acts">
          <ActionButton type="button" className="dl-opt" disabled={busy} onClick={addDomain}>Yes, {domain.domain} is {domain.company}&apos;s</ActionButton>
          <ActionButton type="button" className="hx-link" disabled={busy} onClick={notDomain}>Not theirs</ActionButton>
        </div>
        {domainWrong ? <p className="dl-err" role="alert">{domainWrong}</p> : null}
      </div>
    );
  } else if (domainSaid || domainWrong) {
    mail = <div className="dw-same">{domainSaid ? <p role="status">{domainSaid}</p> : null}{domainWrong ? <p className="dl-err" role="alert">{domainWrong}</p> : null}</div>;
  }

  if (!history && !mail) return null;
  return <>{history}{mail}</>;
}
